import clsx from "clsx";
import { cva, type VariantProps } from "cva";
import {
  type HTMLAttributes,
  type MouseEvent,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import styles from "./index.module.css";

const modalClassName = cva({
  base: styles.modal,
  variants: {
    size: {
      md: styles.md,
      lg: styles.lg,
      full: styles.full,
    },
  },
});

type ModalSize = NonNullable<VariantProps<typeof modalClassName>["size"]>;

const BODY_MODAL_LOCK_ATTR = "data-modal-lock-count";
const MODAL_ROOT_SELECTOR = "[data-modal-root='true']";

export type ModalProps = Omit<HTMLAttributes<HTMLDivElement>, "content"> & {
  open: boolean;
  size?: ModalSize;
  fullScreen?: boolean;
  onRequestClose?: (event?: Event | SyntheticEvent) => void;
  disableClose?: boolean;
  testId?: string;
};

/**
 * 中身のレイアウトを利用側で自由に組み立てるためのモーダル。
 *
 * @remarks
 * 画面全体のフローや、独自構成のコンテンツを載せるときに使う。
 * 確認系の定型 UI には Dialog を使う。
 *
 * @example
 * <Modal open={isOpen} size="full">
 *   <ExampleView />
 * </Modal>
 */
export const Modal = ({
  open,
  size = "md",
  fullScreen = false,
  className,
  disableClose = false,
  onRequestClose,
  testId,
  children,
  ...props
}: ModalProps) => {
  const [mounted, setMounted] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const resolvedSize = fullScreen ? "full" : size;
  const isFull = resolvedSize === "full";

  const isLastModalRoot = useCallback((element: HTMLElement | null) => {
    if (!element) {
      return false;
    }

    return Array.from(document.querySelectorAll<HTMLElement>(MODAL_ROOT_SELECTOR)).at(-1) === element;
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || !open) {
      return;
    }

    const body = document.body;
    const currentCount = Number(body.getAttribute(BODY_MODAL_LOCK_ATTR) ?? "0");
    body.setAttribute(BODY_MODAL_LOCK_ATTR, String(currentCount + 1));

    if (currentCount === 0) {
      body.style.overflow = "hidden";
    }

    return () => {
      const nextCount = Math.max(Number(body.getAttribute(BODY_MODAL_LOCK_ATTR) ?? "1") - 1, 0);
      if (nextCount === 0) {
        body.removeAttribute(BODY_MODAL_LOCK_ATTR);
        body.style.removeProperty("overflow");
        return;
      }

      body.setAttribute(BODY_MODAL_LOCK_ATTR, String(nextCount));
    };
  }, [mounted, open]);

  const handleCancelEvent = useCallback(
    (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      if (disableClose) {
        return;
      }

      onRequestClose?.(event);
    },
    [disableClose, onRequestClose]
  );

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    const listener = (event: Event) => {
      handleCancelEvent(event);
    };

    panel.addEventListener("cancel", listener);
    return () => {
      panel.removeEventListener("cancel", listener);
    };
  }, [handleCancelEvent]);

  const handleOverlayMouseDown = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (disableClose || isFull || event.target !== event.currentTarget) {
        return;
      }

      onRequestClose?.(event);
    },
    [disableClose, isFull, onRequestClose]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      const modal = modalRef.current;
      if (!modal) {
        return;
      }

      if (document.querySelector("dialog[open]")) {
        return;
      }

      if (!isLastModalRoot(modal)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (disableClose) {
        return;
      }

      onRequestClose?.(event);
    };

    document.addEventListener("keydown", handleDocumentKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleDocumentKeyDown, true);
    };
  }, [disableClose, isLastModalRoot, onRequestClose, open]);

  if (!open || !mounted) {
    return null;
  }

  return createPortal(
    <div
      ref={modalRef}
      data-modal-root="true"
      className={clsx(styles.overlay, isFull && styles.overlayFull)}
      onMouseDown={handleOverlayMouseDown}
    >
      <div
        {...props}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        data-testid={testId}
        className={clsx(modalClassName({ size: resolvedSize }), className)}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};
